/**
 * Map `conversations.get` messages into in-memory transcript turns.
 * Verification, decisions, and ratings are rebuilt server-side from traces;
 * this layer only preserves what the channel already returns.
 */
import type {
  ConversationMessage,
  TaskRatingView,
  TraceUsageReceiptView,
  TurnDecisionView,
  SettledQuestionView,
  TurnConfidenceView,
  TurnPolicyView,
  TurnVerificationReply,
} from "./api.js";

export interface WebTurn {
  id?: string;
  role: "user" | "assistant";
  content: string;
  meta?: string | null;
  traceId?: string | null;
  verification?: TurnVerificationReply | null;
  decisions?: TurnDecisionView[];
  /** Policy refusals + a budget pause — rendered as chips, never inline ⛔ prose. */
  policy?: TurnPolicyView | null;
  rating?: TaskRatingView | null;
  changedFiles?: ConversationMessage["changedFiles"];
  usageReceipt?: TraceUsageReceiptView | null;
  /** Measured confidence; null for turns before it existed. */
  confidence?: TurnConfidenceView | null;
  /** Questions the agent asked and what it used. */
  questions?: SettledQuestionView[];
  provider?: string;
  model?: string;
}

/** Split persisted assistant `meta` (`provider · model`) when explicit fields are absent. */
export function parseProviderModel(meta: string | null | undefined): { provider?: string; model?: string } {
  if (!meta || meta === "carried forward" || meta === "sent mid-turn") return {};
  const sep = " · ";
  const idx = meta.indexOf(sep);
  if (idx === -1) return {};
  const provider = meta.slice(0, idx).trim();
  const model = meta.slice(idx + sep.length).trim();
  return {
    ...(provider ? { provider } : {}),
    ...(model ? { model } : {}),
  };
}

export function mapConversationMessagesToTurns(messages: ConversationMessage[]): WebTurn[] {
  return messages.map((m) => {
    const turn: WebTurn = {
      id: m.id,
      role: m.role,
      content: m.content,
      meta: m.meta,
    };
    if (m.role !== "assistant") return turn;

    const fromMeta = parseProviderModel(m.meta);
    turn.traceId = m.traceId ?? null;
    turn.verification = m.verification ?? null;
    turn.decisions = m.decisions ?? [];
    turn.policy = m.policy ?? null;
    turn.confidence = m.confidence ?? null;
    if (m.questions && m.questions.length > 0) turn.questions = m.questions;
    turn.rating = m.rating ?? null;
    turn.changedFiles = m.changedFiles ?? [];
    if (fromMeta.provider) turn.provider = fromMeta.provider;
    if (fromMeta.model) turn.model = fromMeta.model;
    return turn;
  });
}
